import { useEffect, useState, type ReactNode } from 'react';
import { loadAcademyModule } from '../../lib/academy-loader';
import type { ParsedAcademyModule } from '../../lib/academy-content';
import { AcademySegments } from './content-blocks';

interface Props {
  type: 'trading' | 'crypto';
  slug: string | null;
  onLoaded?: (mod: ParsedAcademyModule) => void;
}

export function ModuleContent({ type, slug, onLoaded }: Props): ReactNode {
  const [mod, setMod] = useState<ParsedAcademyModule | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) {
      setMod(null);
      return;
    }
    let alive = true;
    setLoading(true);
    setError(null);
    loadAcademyModule(type, slug)
      .then((m) => {
        if (!alive) return;
        setMod(m);
        onLoaded?.(m);
      })
      .catch((e) => {
        if (alive) setError(e instanceof Error ? e.message : 'Ladefehler');
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
    // onLoaded intentionally excluded — caller passes a stable callback.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type, slug]);

  if (!slug) {
    return (
      <div className="flex h-full items-center justify-center text-slate-500">
        Wähle ein Modul aus der Liste.
      </div>
    );
  }
  if (loading) return <div className="p-6 text-slate-400">Lädt …</div>;
  if (error) return <div className="p-6 text-rose-300">Fehler: {error}</div>;
  if (!mod) return null;

  return (
    <article className="mx-auto max-w-3xl px-4 py-6">
      <header className="mb-6">
        <p className="text-[12px] font-semibold uppercase tracking-[0.14em] text-amber-400">
          {mod.meta.category}
        </p>
        <h1 className="mt-2 text-3xl font-extrabold tracking-tight text-slate-50">
          {mod.meta.title}
        </h1>
      </header>
      <AcademySegments segments={mod.segments} />
    </article>
  );
}
